import { useCallback } from 'react';

import { MarginHistoricOrder, MarginInstrument, MarginPosition } from '@swyftx/margin/build/types';

import { FiatCodeEnum } from '@shared/enums';

import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { useFetchMarginInstruments } from './useFetchMarginInstruments';
import { useSwyftxMarginStore } from '../../state';

const useMarginInstruments = () => {
  const { data, isFetching, isLoading } = useFetchMarginInstruments();
  const { tradeCurrency } = useSwyftxMarginStore();
  const { getAssetByUuid, getAssetByCode, getAssetsByUuid } = useMarkets();

  const getMarketBySecondaryCode = useCallback(
    (secondary?: string) => {
      if (!data || !secondary) return undefined;

      const secondaryAsset = getAssetByCode(secondary);

      return data.find((d) => d.secondaryAssetUuid === secondaryAsset?.assetUuid);
    },
    [data, getAssetByCode],
  );

  const getMarketById = useCallback(
    (id?: string | null) => {
      if (!data || !id) return undefined;
      return data.find((d) => d.uuid === id);
    },
    [data],
  );

  const getMarketByOrder = useCallback(
    (order?: MarginHistoricOrder) => {
      if (!data || !order) return undefined;

      return getMarketById(order.marketUuid);
    },
    [data, getMarketById],
  );

  const getMarketByPosition = useCallback(
    (position?: MarginPosition) => {
      if (!data || !position) return undefined;

      return getMarketById(position.marketUuid);
    },
    [data, getMarketById],
  );

  const getInstrumentSymbol = useCallback(
    (instrument: MarginInstrument) =>
      getAssetsByUuid([instrument.secondaryAssetUuid, instrument.primaryAssetUuid])
        .map((a) => a.code)
        .join(''),
    [getAssetsByUuid],
  );

  const getInstrumentName = useCallback(
    (instrument: MarginInstrument) =>
      getAssetsByUuid([instrument.secondaryAssetUuid, instrument.primaryAssetUuid])
        .map((a) => a.name)
        .join(' / '),
    [getAssetsByUuid],
  );

  const selectedMarket = getMarketBySecondaryCode(tradeCurrency);

  const selectedMarketAsset = tradeCurrency ? getAssetByCode(tradeCurrency) : undefined;

  const selectedMarketPrimary = tradeCurrency ? getAssetByUuid(selectedMarket?.primaryAssetUuid) : undefined;

  const selectedMarketSecondary = tradeCurrency ? getAssetByUuid(selectedMarket?.secondaryAssetUuid) : undefined;

  return {
    markets: data,
    isFetching,
    isLoading,
    getMarketBySecondaryCode,
    getMarketByPosition,
    getMarketByOrder,
    getMarketById,
    getInstrumentSymbol,
    getInstrumentName,
    selectedMarket,
    selectedMarketAsset,
    selectedMarketPrimary,
    selectedMarketSecondary,
    primaryCode: selectedMarketPrimary?.code || FiatCodeEnum.USD,
    secondaryCode: selectedMarketSecondary?.code,
  };
};

export { useMarginInstruments };
